import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Loader2, Save, Lock } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import ProtectedContent from '../components/ProtectedContent';
import useAllowPrint from '../components/useAllowPrint';

/**
 * Private "My Relapse Prevention Plan".
 * Visible ONLY to the user who owns it (per /api/relapse-prevention/plan).
 * No admin ever sees the free-text answers.
 */

const emptyPlan = {
  triggers: '',
  early_warnings: '',
  coping_strategies: '',
  support_contacts: '',
  safe_places: '',
  reasons_to_stay: '',
  halt_notes: '',
};

const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));
const fromLines = (s) => (s || '').split('\n').map((l) => l.trim()).filter(Boolean);

export default function RelapsePreventionPage() {
  useAllowPrint();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(emptyPlan);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/relapse-prevention/plan`);
        if (r.ok) {
          const d = await r.json();
          setPlan({
            triggers: toLines(d.triggers),
            early_warnings: toLines(d.early_warnings),
            coping_strategies: toLines(d.coping_strategies),
            support_contacts: toLines(d.support_contacts),
            safe_places: toLines(d.safe_places),
            reasons_to_stay: d.reasons_to_stay || '',
            halt_notes: d.halt_notes || '',
          });
          setUpdatedAt(d.updated_at || null);
        }
      } catch (_) { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        triggers: fromLines(plan.triggers),
        early_warnings: fromLines(plan.early_warnings),
        coping_strategies: fromLines(plan.coping_strategies),
        support_contacts: fromLines(plan.support_contacts),
        safe_places: fromLines(plan.safe_places),
        reasons_to_stay: (plan.reasons_to_stay || '').trim(),
        halt_notes: (plan.halt_notes || '').trim(),
      };
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/relapse-prevention/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Save failed');
      const d = await r.json();
      setUpdatedAt(d.updated_at);
      toast.success('Plan saved');
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, hint, value, onChange, rows = 3, testId }) => (
    <div className="mb-4">
      <label className="block text-base font-bold text-black mb-1">{label}</label>
      {hint && <p className="text-sm text-black mb-2">{hint}</p>}
      <Textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-2 border-black bg-white text-base text-black"
        data-testid={testId}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="relapse-prevention-page">
      <PageHeader title="Relapse Prevention" backTo="/library" icon={Shield} />

      <main className="pt-20 px-4">
        <div className="max-w-2xl mx-auto space-y-4">

          {/* Why this matters */}
          <ProtectedContent>
            <section className="bg-white border-2 border-black rounded-xl p-5" data-testid="rp-intro">
              <h2 className="text-lg font-bold text-black mb-2">Why this plan matters</h2>
              <p className="text-base text-black leading-relaxed">
                Relapse rarely starts the moment someone picks up a drink or drug. It usually begins days
                or weeks earlier, in small emotional and mental shifts. This plan is a page you can come
                back to on a hard day, so the thinking is already done. Nobody else sees it. It is for you.
              </p>
            </section>
          </ProtectedContent>

          {/* HALT */}
          <ProtectedContent>
            <section className="bg-white border-2 border-black rounded-xl p-5" data-testid="rp-halt">
              <h2 className="text-lg font-bold text-black mb-2">HALT: daily four-question check</h2>
              <p className="text-base text-black leading-relaxed mb-3">
                Before you act on a strong feeling or an urge, ask yourself:
              </p>
              <ul className="list-disc list-inside space-y-1 text-base text-black">
                <li><strong>H</strong>ungry: have I eaten in the last few hours?</li>
                <li><strong>A</strong>ngry: am I carrying anger I have not named yet?</li>
                <li><strong>L</strong>onely: have I spoken to anyone from Recovery today?</li>
                <li><strong>T</strong>ired: have I slept properly in the last 24 hours?</li>
              </ul>
              <p className="text-base text-black leading-relaxed mt-3">
                If the answer to any of these is "no", that is the first thing to fix. HALT states magnify
                cravings. Dealing with the HALT first is often the whole intervention.
              </p>
            </section>
          </ProtectedContent>

          {/* Private plan form */}
          <section className="bg-white border-2 border-black rounded-xl p-5" data-testid="rp-plan-form">
            <div className="flex items-start gap-2 mb-3">
              <Lock className="w-4 h-4 text-black mt-1" />
              <div>
                <h2 className="text-lg font-bold text-black">My Relapse Prevention Plan</h2>
                <p className="text-sm text-black">Private to you. Not shared with admins or sponsors.</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : (
              <>
                <Field
                  label="My triggers"
                  hint="One per line. People, places, times, feelings, songs, situations that pull me toward using."
                  value={plan.triggers}
                  onChange={(v) => setPlan({ ...plan, triggers: v })}
                  rows={4}
                  testId="rp-triggers"
                />
                <Field
                  label="My early warning signs"
                  hint="One per line. What I notice first, before a craving gets loud: isolating, skipping meetings, poor sleep…"
                  value={plan.early_warnings}
                  onChange={(v) => setPlan({ ...plan, early_warnings: v })}
                  rows={4}
                  testId="rp-early-warnings"
                />
                <Field
                  label="My coping actions"
                  hint="One per line. Things I will DO when a trigger lands: call sponsor, go for a walk, ring a Recovery friend, urge-surf for 20 minutes."
                  value={plan.coping_strategies}
                  onChange={(v) => setPlan({ ...plan, coping_strategies: v })}
                  rows={4}
                  testId="rp-coping"
                />
                <Field
                  label="People I will contact (names + numbers)"
                  hint="One per line. Sponsor, trusted Recovery friends, GP, local AA/NA contact."
                  value={plan.support_contacts}
                  onChange={(v) => setPlan({ ...plan, support_contacts: v })}
                  rows={4}
                  testId="rp-support"
                />
                <Field
                  label="Safe places I can go"
                  hint="One per line. Meeting locations, a friend's house, a public library, a coffee shop."
                  value={plan.safe_places}
                  onChange={(v) => setPlan({ ...plan, safe_places: v })}
                  rows={3}
                  testId="rp-safe-places"
                />
                <Field
                  label="My reasons to stay clean and sober today"
                  hint="A short paragraph I can read on a bad day."
                  value={plan.reasons_to_stay}
                  onChange={(v) => setPlan({ ...plan, reasons_to_stay: v })}
                  rows={4}
                  testId="rp-reasons"
                />
                <Field
                  label="HALT notes"
                  hint="Anything personal about Hungry/Angry/Lonely/Tired I want to remember."
                  value={plan.halt_notes}
                  onChange={(v) => setPlan({ ...plan, halt_notes: v })}
                  rows={3}
                  testId="rp-halt-notes"
                />

                <div className="flex items-center gap-3 mt-5">
                  <Button
                    onClick={save}
                    disabled={saving}
                    className="rounded-lg px-5 py-2.5 text-base font-bold border-2 border-black text-black hover:brightness-95"
                    style={{ backgroundColor: '#ffcd04' }}
                    data-testid="rp-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2 inline" /> : <Save className="w-4 h-4 mr-2 inline" />}
                    Save plan
                  </Button>
                  {updatedAt && (
                    <span className="text-sm text-black">
                      Last saved {new Date(updatedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </>
            )}
          </section>

          {/* If you are at risk right now */}
          <section className="bg-white border-2 border-black rounded-xl p-5" data-testid="rp-at-risk">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-black mt-0.5" />
              <div>
                <h2 className="text-lg font-bold text-black mb-1">If you are at risk right now</h2>
                <p className="text-base text-black leading-relaxed mb-3">
                  If you feel you could use today, call someone on your plan before anything else. If you
                  are unsafe, open Get help now.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/get-help')}
                  className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg py-3 px-4 text-base font-bold border-2 border-black"
                  data-testid="rp-get-help-btn"
                >
                  Get help now
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
